import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faEllipsis, faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCircleCheck as faCircleCheckOutline } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	ButtonStandard,
	DropDownMenu,
	type DropDownMenuOption,
	type ICellRendererParams,
	Icon,
	ModalBase,
	type PYColumnDef,
	PageBanner,
	Table,
	Text,
	Tooltip,
} from "@performyard/py-components";

import TextInput from "../../../../../components/Inputs/InputText";
import Spacer from "../../../../../components/Spacer";
import CustomFactorModal from "./components/CustomFactorModal";

import {
	useDeleteEngagementCustomFactor,
	useEngagementCustomFactors,
} from "../../../../../api/hooks/defs/engagement-custom-factors";

import { CustomFactor } from "../../../../../api/services/defs/engagement-custom-factors";

import { caseInsensitiveComparator } from "../EngagementSetupUtils";
import css from "../SurveyQuestions/SurveyQuestions.module.scss";

// --- Cell Renderers ---

const OverallSatisfactionCellRenderer = (params: ICellRendererParams<CustomFactor>) => {
	if (!params.data) return null;
	const included = params.data.include_in_overall_satisfaction;
	return (
		<div className="flex size-full min-h-0 items-center justify-center">
			<Tooltip
				content={
					included
						? "This factor is included in engagement satisfaction metrics"
						: "This factor is not included in engagement satisfaction metrics"
				}
				dangerouslySetClassName="max-w-[151px]"
			>
				<FontAwesomeIcon
					icon={included ? faCircleCheck : faCircleCheckOutline}
					color={included ? "var(--py-primary-5)" : "var(--py-neutral-3)"}
				/>
			</Tooltip>
		</div>
	);
};

const ActionsCellRenderer = (params: ICellRendererParams<CustomFactor>) => {
	if (!params.data) return null;
	const { handleAction } = params.context;
	const factor = params.data;

	const options: DropDownMenuOption[] = [
		{ key: "edit", label: "Edit" },
		{ key: "delete", label: "Delete" },
	];
	return (
		<DropDownMenu
			options={options}
			onSelect={(key: string) => handleAction(key, factor)}
			placement="bottom-end"
			minWidth={80}
		>
			<Icon icon={faEllipsis} size={24} color="neutral-5" />
		</DropDownMenu>
	);
};

// --- Column Definitions ---

const createColumnDefs = (): PYColumnDef<CustomFactor>[] => [
	{
		headerName: "Factor",
		field: "name",
		type: "text",
		sortable: true,
		width: 120,
		resizable: false,
		suppressSizeToFit: true,
		comparator: caseInsensitiveComparator,
	},
	{
		headerName: "Description",
		field: "description",
		type: "text",
		sortable: true,
		minWidth: 425,
		flex: 1,
		resizable: false,
		comparator: caseInsensitiveComparator,
	},
	{
		headerName: "Date Created",
		field: "created_at",
		type: "date",
		sortable: true,
		width: 115,
		resizable: false,
		suppressSizeToFit: true,
	},
	{
		headerName: "Last Modified",
		field: "updated_at",
		type: "date",
		sortable: true,
		width: 115,
		resizable: false,
		suppressSizeToFit: true,
	},
	{
		headerName: "Modified by",
		field: "updated_by_employee",
		type: "employee",
		sortable: true,
		width: 140,
		resizable: false,
		suppressSizeToFit: true,
	},
	{
		headerName: "",
		field: "include_in_overall_satisfaction",
		cellRenderer: OverallSatisfactionCellRenderer,
		sortable: true,
		width: 60,
		resizable: false,
		suppressSizeToFit: true,
	},
	{
		headerName: "",
		field: "options",
		cellRenderer: ActionsCellRenderer,
		cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
		sortable: false,
		width: 50,
		resizable: false,
		suppressSizeToFit: true,
	},
];

const CustomFactors: React.FC = () => {
	const [deleteFactorId, setDeleteFactorId] = useState<string | null>(null);
	const [editFactorId, setEditFactorId] = useState<string | null>(null);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [searchValue] = useDebounceValue(search, 250);

	const { data, refetch, isLoading } = useEngagementCustomFactors();

	const customFactors = data?.customFactors || [];
	const maxAllowedFactors = data?.maxAllowedFactors || 10;
	const maxOverallSatisfactionFactors = data?.maxOverallSatisfactionFactors || 3;
	const currentOverallSatisfactionCount = data?.currentOverallSatisfactionCount || 0;

	const { mutate: deleteFactor } = useDeleteEngagementCustomFactor({
		onSuccess: () => {
			refetch();
			setDeleteFactorId(null);
		},
	});

	const filteredFactors = useMemo(() => {
		if (!searchValue) return customFactors;

		const lowerSearch = searchValue.toLowerCase();
		return customFactors.filter(
			(factor) =>
				factor.name.toLowerCase().includes(lowerSearch) ||
				(factor.description && factor.description.toLowerCase().includes(lowerSearch))
		);
	}, [customFactors, searchValue]);

	// Derive editFactor from latest fetched data so modal always has fresh values
	const editFactor = editFactorId ? (customFactors.find((f) => f._id === editFactorId) ?? null) : null;

	const resetModals = () => {
		setEditFactorId(null);
		setIsCreateModalOpen(false);
	};

	const handleDeleteConfirm = () => {
		if (deleteFactorId) {
			deleteFactor(deleteFactorId);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteFactorId(null);
	};

	const handleAction = useCallback((key: string, factor: CustomFactor) => {
		if (key === "edit") {
			setEditFactorId(factor._id);
		} else if (key === "delete") {
			setDeleteFactorId(factor._id);
		}
	}, []);

	const columnDefs = useMemo(() => createColumnDefs(), []);

	const gridContext = useMemo(() => ({ handleAction }), [handleAction]);

	const isMaxFactorsReached = customFactors.length >= maxAllowedFactors;

	const BANNER_DISMISSED_KEY = "custom-factors-max-banner-dismissed";

	const [isBannerDismissed, setIsBannerDismissed] = useState(
		() => sessionStorage.getItem(BANNER_DISMISSED_KEY) === "true"
	);

	const handleBannerDismiss = useCallback(() => {
		setIsBannerDismissed(true);
		sessionStorage.setItem(BANNER_DISMISSED_KEY, "true");
	}, []);

	useEffect(() => {
		if (!isMaxFactorsReached) {
			setIsBannerDismissed(false);
			sessionStorage.removeItem(BANNER_DISMISSED_KEY);
		}
	}, [isMaxFactorsReached]);

	return (
		<div className={css.main}>
			<Spacer height={3} />
			{isMaxFactorsReached && !isBannerDismissed && (
				<>
					<PageBanner
						color="primary"
						size="small"
						mainTitle="You have reached the maximum number of Custom Factors."
						subTitle="Delete an existing custom factor to create a new one."
						icon={faTriangleExclamation}
						onDismiss={handleBannerDismiss}
					/>
					<Spacer height={2} />
				</>
			)}
			<div className={css.tableControls}>
				<TextInput
					className={css.search}
					value={search}
					onChange={setSearch}
					placeholder="Search factors..."
					icon={faSearch}
					clearable
				/>
				<div>
					<ButtonStandard
						onClick={() => setIsCreateModalOpen(true)}
						color="primary"
						iconLeft={faPlus}
						disabled={isMaxFactorsReached}
					>
						Custom Factor
					</ButtonStandard>
				</div>
			</div>
			<Spacer height={2} />
			<div className={`${css.table} [&_.ag-center-cols-viewport]:!min-h-0`}>
				<Table<CustomFactor>
					autoHeight
					rowData={filteredFactors}
					columnDefs={columnDefs}
					loading={isLoading}
					getRowId={(params) => params.data._id}
					emptyStateText="No Custom Factors Found"
					rowHeight={48}
					context={gridContext}
					initialState={{
						sort: {
							sortModel: [{ colId: "name", sort: "asc" }],
						},
					}}
					onGridReady={(params) => params.api.sizeColumnsToFit()}
				/>
			</div>
			<ModalBase isOpen={deleteFactorId !== null} onClose={handleDeleteCancel}>
				<div className="w-[432px] px-6 pb-6">
					<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
						Delete Custom Factor
					</Text>
					<Text size="paragraph-1" weight="regular" dangerouslySetClassName="mt-4 text-center">
						Are you sure you want to delete this factor? Custom questions will become unassigned upon
						deletion.
					</Text>
					<div className="mt-6 flex justify-center gap-2">
						<div>
							<ButtonStandard variant="outline" color="neutral" size="large" onClick={handleDeleteCancel}>
								Cancel
							</ButtonStandard>
						</div>
						<div>
							<ButtonStandard color="primary" size="large" onClick={handleDeleteConfirm}>
								Yes, Delete Factor
							</ButtonStandard>
						</div>
					</div>
				</div>
			</ModalBase>
			<CustomFactorModal
				isOpen={isCreateModalOpen || editFactor !== null}
				onClose={resetModals}
				onSuccess={() => {
					resetModals();
					refetch();
				}}
				factor={editFactor}
				currentOverallSatisfactionCount={currentOverallSatisfactionCount}
				maxOverallSatisfactionFactors={maxOverallSatisfactionFactors}
				maxAllowedFactors={maxAllowedFactors}
			/>
		</div>
	);
};

export default CustomFactors;
